import {
  BridgeData,
  CurrencyPreset,
  ExternalAccount,
  KycTypePreset,
  StatePreset,
} from '~/app/settings/team/banks/BanksSimulator/types'
import { BridgeVirtualAccount } from '~/types'

// ---------------------------------------------------------------------------
// Mock data builders
// ---------------------------------------------------------------------------

const mockVirtualAccount = (
  address: string,
  currency: 'usd' | 'eur',
): BridgeVirtualAccount => ({
  id: `sim_va_${currency === 'usd' ? 'us' : 'eu'}_001`,
  destinationAddress: address,
  destinationToken:
    currency === 'usd'
      ? '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913'
      : '0x60a3E35Cc302bFA44Cb288Bc5a4F316Fdb1adb42',
  destinationChainId: 8453,
  sourceData:
    currency === 'usd'
      ? {
          paymentRails: ['ach'],
          currency: 'usd',
          bankName: 'Sim Bank US',
          bankAddress: '123 Simulation St, New York, NY 10001',
          bankRoutingNumber: '021000021',
          bankAccountNumber: '1234567890',
          bankBeneficiaryName: 'Splits Inc',
          bankBeneficiaryAddress: '456 Splits Ave, San Francisco, CA 94105',
        }
      : {
          paymentRails: ['sepa'],
          currency: 'eur',
          bankName: 'Sim Bank EU',
          bankAddress: '10 Rue de Simulation, 75001 Paris',
          bic: 'SIMGFRPP',
          iban: 'FR7630006000011234567890189',
          bankBeneficiaryName: 'Splits Inc EU',
          bankBeneficiaryAddress: '20 Splits Strasse, 10115 Berlin',
        },
})

const MOCK_INTERNAL_ACCOUNT: ExternalAccount = {
  externalAccountId: 'sim_ext_001',
  name: 'Sim Checking Account',
  accountNumberLastFour: '4321',
  source: 'internal',
  liquidationAddress: '0xaabbccdd11223344aabbccdd11223344aabbccdd',
  liquidationToken: '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913',
  liquidationChainId: 8453,
}

const MOCK_EXTERNAL_ACCOUNT: ExternalAccount = {
  externalAccountId: 'sim_ext_002',
  name: 'Sim External Savings',
  accountNumberLastFour: '8765',
  source: 'external',
  liquidationAddress: '0xddeeff0011223344ddeeff0011223344ddeeff00',
  liquidationToken: '0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913',
  liquidationChainId: 8453,
}

const makeBridge = (
  overrides?: Partial<NonNullable<BridgeData>>,
): BridgeData => ({
  customerId: 'sim_001',
  addressCollectionNeeded: false,
  onRampPaused: false,
  kyc: {
    kycType: 'individual',
    kycLinkId: 'sim_link',
    tosLinkUrl: '#',
    tosStatus: 'pending',
    tosUpdatedAt: new Date(),
    kycLinkUrl: '#',
    kycStatus: 'incomplete',
    kycUpdatedAt: new Date(),
  },
  customerLocales: [{ locale: 'us', destinationCurrency: 'usdc' }],
  virtualAccounts: [],
  externalAccounts: [],
  ...overrides,
})

const buildKyc = (
  tosStatus: string,
  kycStatus: string,
  kycType: KycTypePreset = 'individual',
) => ({
  kyc: {
    kycType,
    kycLinkId: 'sim_link',
    tosLinkUrl: '#',
    tosStatus,
    tosUpdatedAt: new Date(),
    kycLinkUrl: '#',
    kycStatus,
    kycUpdatedAt: new Date(),
  },
})

const buildLocaleData = (currency: CurrencyPreset, address: string) => {
  const locales =
    currency === 'both'
      ? (['us', 'eu'] as const)
      : currency === 'eu'
        ? (['eu'] as const)
        : (['us'] as const)

  return {
    customerLocales: locales.map((l) => ({
      locale: l as 'us' | 'eu',
      destinationCurrency: l === 'us' ? 'usdc' : 'eurc',
    })),
    virtualAccounts: locales.map((l) =>
      mockVirtualAccount(address, l === 'us' ? 'usd' : 'eur'),
    ),
  }
}

// ---------------------------------------------------------------------------
// State presets
// ---------------------------------------------------------------------------

export const STATES: StatePreset[] = [
  {
    id: 'not-started',
    label: 'Not started',
    build: () => undefined,
  },
  {
    id: 'tos-incomplete',
    label: 'TOS incomplete',
    build: (c, t, a) =>
      makeBridge({
        ...buildKyc('pending', 'not_started', t),
        ...buildLocaleData(c, a),
        virtualAccounts: [],
      }),
  },
  {
    id: 'kyc-incomplete',
    label: 'KYC started',
    build: (c, t, a) =>
      makeBridge({
        ...buildKyc('approved', 'incomplete', t),
        ...buildLocaleData(c, a),
        virtualAccounts: [],
      }),
  },
  {
    id: 'review',
    label: 'Under review',
    build: (c, t, a) =>
      makeBridge({
        ...buildKyc('approved', 'under_review', t),
        ...buildLocaleData(c, a),
        virtualAccounts: [],
      }),
  },
  {
    id: 'approved',
    label: 'Approved',
    build: (c, t, a) =>
      makeBridge({
        ...buildKyc('approved', 'approved', t),
        ...buildLocaleData(c, a),
        externalAccounts: [MOCK_INTERNAL_ACCOUNT, MOCK_EXTERNAL_ACCOUNT],
      }),
  },
  {
    id: 'address-needed',
    label: 'Addr needed',
    build: (c, t, a) =>
      makeBridge({
        ...buildKyc('approved', 'approved', t),
        ...buildLocaleData(c, a),
        addressCollectionNeeded: true,
        externalAccounts: [MOCK_INTERNAL_ACCOUNT],
      }),
  },
  {
    id: 'rejected',
    label: 'Rejected',
    build: (c, t, a) =>
      makeBridge({
        ...buildKyc('approved', 'rejected', t),
        ...buildLocaleData(c, a),
        virtualAccounts: [],
      }),
  },
  {
    id: 'paused',
    label: 'Paused',
    build: (c, t, a) =>
      makeBridge({
        ...buildKyc('approved', 'paused', t),
        ...buildLocaleData(c, a),
        virtualAccounts: [],
      }),
  },
]

export const KYC_TYPES: { id: KycTypePreset; label: string }[] = [
  { id: 'individual', label: 'KYC' },
  { id: 'business', label: 'KYB' },
]

export const CURRENCIES: { id: CurrencyPreset; label: string }[] = [
  { id: 'us', label: 'US' },
  { id: 'eu', label: 'EU' },
  { id: 'both', label: 'Both' },
]
